// StatsFilterKit — the shared "compare-against" filter engine, ported from the
// prototype's window.StatsFilterKit (js/renderers-stats.js). Ben's rule: the two
// shared engines (StatsFilterKit, EventMapKit) are ported ONCE as a shared module,
// never copied per renderer. Filter, Rankings, Comparison, Similarity and Stats all
// reuse it.
//
// Scope of THIS module (04g-1, "hybrid" decision): the PURE engine — the tuned
// filter/value-mode logic, ported verbatim and unit-tested. The kit's two DOM UI
// functions (renderFilterBar / renderAddMenu and their popovers) are React since 04h-1
// (<FilterChipBar>, FilterPopovers); renderers-stats.js itself was deleted in 05k-2 (the
// line citations below point at it — `git show 7dc6fdf:packages/app-web-proto/public/js/
// renderers-stats.js`). See docs/frontend-v3/phase-04-bridge.md.
//
// Data-injection deviation from the verbatim source (deliberate, per Ben's "the data
// shape is the contract, not the source"): the prototype's population()/getFilterOptions
// read window.CatalystData directly. Here the population is passed IN (getFilterOptions
// takes the player list) so the engine is pure, testable, and unchanged at the Track-2
// cutover — the calling hook supplies the players (useAllPlayers today, app-api later).

// A snapshot player record, in the fields the engine actually reads. Loose on purpose
// (the kit is renderer-adjacent logic): `stats` is the flat per-player metric map.
export interface KitPlayer {
  positionTemplate?: string | null;
  competition?: string | null;
  /** Her league for the League chip, when `competition` is used for a display label instead (a
   *  live position row reads "at Midfielder"). Absent → `competition` (the snapshot's players). */
  league?: string | null;
  team?: string | null;
  country?: string | null;
  dob?: string | null;
  height?: number | null;
  weight?: number | null;
  stats?: Record<string, number | null | undefined>;
}

/** A metric from the catalogue, in the fields the value-mode helpers read. */
export interface KitMetric {
  key: string;
  outputType?: string;
}

/** Reverse filter: "exclude just this one value, keep everything else." */
export interface ExcludeValue {
  exclude: true;
  value: string;
}
// A list = "any of these" (Ben, 2026-10-16: tick several values on a chip, e.g. Full Back +
// Centre Back, or two leagues). Only the live site's picker makes lists; the demo keeps one value.
export type CategoricalActive = string | string[] | ExcludeValue;
export interface RangeActive {
  min: number;
  max: number;
}
/** A module's live filter state — categorical values keyed by filter, range objects
 *  keyed by range filter. Absent key = filter not applied. */
export type ActiveFilters = Record<string, CategoricalActive | RangeActive | undefined>;

export interface RangeFilterSpec {
  label: string;
  chipLabel: string;
  unit: string;
  /** Relative filters only (the player-relative "same age" midpoint). */
  zeroLabel?: string;
  relative: boolean;
  domain: { min: number; max: number };
  getValue: (p: KitPlayer) => number | null;
  /** Relative filters only — absolute configs (e.g. Rankings') omit it. */
  formatOffset?: (v: number) => string;
  /** Optional: the chip's own words for its range (the live Stats Minutes chip: "150+ min").
   *  Without it, the readout below is used. */
  formatReadout?: (range: RangeActive) => string;
}
export type RangeFilterConfig = Record<string, RangeFilterSpec>;

const DASH = "—";

// --- small formatters (per-file dupes in the prototype; one home here) ---------

/** A metric value formatted by its declared outputType (renderers-stats.js:2). */
export function formatMetricValue(raw: number | null | undefined, outputType?: string): string {
  if (raw === undefined || raw === null || Number.isNaN(raw)) return DASH;
  if (outputType === "percentage") return (raw * 100).toFixed(1) + "%";
  if (outputType === "integer") return Math.round(raw).toLocaleString();
  return Math.abs(raw) >= 10 ? raw.toFixed(1) : raw.toFixed(2);
}

// The kit's OWN position humaniser: returns "" (not "—") for an absent template,
// because getFilterOptions/matchesFilters compare humanised position strings and an
// em-dash would leak into the option list. Kept separate from bio-view's on purpose.
function humanizePositionTemplate(tpl: string | null | undefined): string {
  if (!tpl) return "";
  return tpl
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function ageFromDob(dob: string | null | undefined): number | null {
  if (!dob) return null;
  const d = new Date(dob);
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const monthDiff = now.getMonth() - d.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < d.getDate())) age--;
  return age;
}

// Preferred foot is derived (no explicit field): left_foot_perc gated by passes_90 as
// a minimum-sample guard. Below the gate → null ("not classified"), which
// matchesCategorical treats as matching neither Left nor Right (renderers-stats.js:201).
const FOOT_MIN_PASSES_90 = 3;
export function preferredFoot(pl: KitPlayer): string | null {
  const s = pl.stats || {};
  const passes = s.passes_90;
  const leftPct = s.left_foot_perc;
  if (passes == null || Number.isNaN(passes) || passes <= FOOT_MIN_PASSES_90) return null;
  if (leftPct == null || Number.isNaN(leftPct)) return null;
  return leftPct > 0.5 ? "Left-footed" : "Right-footed";
}

// --- Per 90 / Total value mode ------------------------------------------------

/** Only "_90" metrics have a Total view to switch to (renderers-stats.js:25). */
export function hasTotalMode(metric: KitMetric): boolean {
  return metric.key.endsWith("_90");
}

/**
 * The raw total behind a per-90 rate = per90 * nineties_played (reversing exactly the
 * division that produced the rate), noise-cleaned. NOT read from a raw field — that
 * only exists for the original hero players (renderers-stats.js:28).
 */
export function resolveTotalValue(
  statsObj: Record<string, number | null | undefined>,
  metric: KitMetric,
): number | null {
  const per90 = statsObj[metric.key];
  const nineties = statsObj.nineties_played;
  if (per90 == null || Number.isNaN(per90) || !nineties) return null;
  return Math.round(per90 * nineties * 1e6) / 1e6;
}

/** A metric's value under the active mode — "total" only applies where hasTotalMode. */
export function metricValueForMode(
  statsObj: Record<string, number | null | undefined>,
  metric: KitMetric,
  mode: string,
): number | null | undefined {
  if (mode === "total" && hasTotalMode(metric)) return resolveTotalValue(statsObj, metric);
  return statsObj[metric.key];
}

/** A raw total formatted by what the number looks like (integer vs genuine decimal),
 *  since the catalogue declares no outputType for totals (renderers-stats.js:52). */
export function formatTotalValue(raw: number | null | undefined): string {
  if (raw === undefined || raw === null || Number.isNaN(raw)) return DASH;
  if (Number.isInteger(raw)) return raw.toLocaleString();
  return Math.abs(raw) >= 10 ? raw.toFixed(1) : raw.toFixed(2);
}

export function formatValueForMode(
  raw: number | null | undefined,
  metric: KitMetric,
  mode: string,
): string {
  return mode === "total" && hasTotalMode(metric)
    ? formatTotalValue(raw)
    : formatMetricValue(raw, metric.outputType);
}

// --- range filters (Age / Height / Weight, player-relative) -------------------

export const RANGE_FILTERS: RangeFilterConfig = {
  age: {
    label: "Age range",
    chipLabel: "Age",
    unit: "y",
    zeroLabel: "same age",
    relative: true,
    domain: { min: -15, max: 15 },
    getValue: (p) => ageFromDob(p.dob),
    formatOffset: (v) => (v === 0 ? "same age" : v < 0 ? `${-v}y younger` : `${v}y older`),
  },
  height: {
    label: "Height range",
    chipLabel: "Height",
    unit: "cm",
    zeroLabel: "same height",
    relative: true,
    domain: { min: -20, max: 20 },
    getValue: (p) => (typeof p.height === "number" ? p.height : null),
    formatOffset: (v) => (v === 0 ? "same height" : v < 0 ? `${-v}cm shorter` : `${v}cm taller`),
  },
  weight: {
    label: "Weight range",
    chipLabel: "Weight",
    unit: "kg",
    zeroLabel: "same weight",
    relative: true,
    domain: { min: -20, max: 20 },
    getValue: (p) => (typeof p.weight === "number" ? p.weight : null),
    formatOffset: (v) => (v === 0 ? "same weight" : v < 0 ? `${-v}kg lighter` : `${v}kg heavier`),
  },
};

export function isRangeFilter(key: string, rangeFilters: RangeFilterConfig = RANGE_FILTERS): boolean {
  return Object.prototype.hasOwnProperty.call(rangeFilters, key);
}

export function formatRangeReadout(
  key: string,
  range: RangeActive,
  rangeFilters: RangeFilterConfig = RANGE_FILTERS,
): string {
  const spec = rangeFilters[key];
  if (!spec) throw new Error(`formatRangeReadout: unknown range filter "${key}"`);
  if (spec.formatReadout) return spec.formatReadout(range);
  if (!spec.relative) {
    // Absolute range (e.g. Ranking's) — plain "22y to 30y", no reference player.
    return range.min === range.max
      ? `Exactly ${range.min}${spec.unit}`
      : `${range.min}${spec.unit} to ${range.max}${spec.unit}`;
  }
  // Relative specs always carry zeroLabel + formatOffset (the optional fields exist
  // only so absolute configs can omit them).
  const { zeroLabel, formatOffset } = spec;
  if (!zeroLabel || !formatOffset) throw new Error(`formatRangeReadout: relative filter "${key}" missing zeroLabel/formatOffset`);
  if (range.min === range.max) {
    const label = zeroLabel.charAt(0).toUpperCase() + zeroLabel.slice(1);
    return range.min === 0 ? `${label} only` : `Exactly ${formatOffset(range.min)}`;
  }
  const lo = formatOffset(range.min);
  return lo.charAt(0).toUpperCase() + lo.slice(1) + " to " + formatOffset(range.max);
}

// --- categorical filters ------------------------------------------------------

export const FILTER_LABELS: Record<string, string> = {
  position: "Position",
  league: "League",
  club: "Club",
  nationality: "Nationality",
  foot: "Preferred Foot",
};

export function excludeValue(value: string): ExcludeValue {
  return { exclude: true, value };
}
export function isExcludeValue(v: unknown): v is ExcludeValue {
  return v !== null && typeof v === "object" && (v as ExcludeValue).exclude === true;
}
export function categoricalValueOf(v: CategoricalActive | undefined): string | undefined {
  if (Array.isArray(v)) return v[0];
  return isExcludeValue(v) ? v.value : (v as string | undefined);
}
/** The values a chip includes ("any of these"); [] for none or an exclude. */
export function selectedValuesOf(v: CategoricalActive | undefined): string[] {
  if (v === undefined || isExcludeValue(v)) return [];
  return Array.isArray(v) ? v : [v];
}
/** Is this chip filtering? Not when absent, nor when just added with nothing ticked (`[]`). */
export function chipIsOn(v: ActiveFilters[string]): boolean {
  return v !== undefined && !(Array.isArray(v) && v.length === 0);
}
/** One value stays a plain string (what the demo and older saved state hold); several → a list. */
export function categoricalFromValues(values: readonly string[]): CategoricalActive | undefined {
  if (values.length === 0) return undefined;
  return values.length === 1 ? values[0]! : [...values];
}
/** actual matches the active selection: absent → all; exclude → anything but; a list → any of
 *  them; else equal. */
export function matchesCategorical(actual: string | null, active: CategoricalActive | undefined): boolean {
  if (active === undefined) return true;
  if (isExcludeValue(active)) return actual !== active.value;
  // An empty list is a chip just added, nothing ticked yet: no filter (live, Ben 2026-10-17).
  if (Array.isArray(active)) return active.length === 0 || (actual !== null && active.includes(actual));
  return actual === active;
}
export function formatCategoricalValue(v: CategoricalActive): string {
  if (Array.isArray(v)) {
    if (v.length === 0) return "choose…"; // just added, nothing ticked yet (live League chip)
    // Two or more: just the count, "League: 5" (Ben, 2026-10-17); the list shows which.
    return v.length === 1 ? v[0]! : String(v.length);
  }
  return isExcludeValue(v) ? `Not ${v.value}` : v;
}

/** Distinct attribute values across the population, per categorical filter. Population
 *  is passed in (not read from window) — the data-injection deviation noted up top. */
export function getFilterOptions(players: readonly KitPlayer[]): Record<string, string[]> {
  const uniqSorted = (fn: (pl: KitPlayer) => string | null | undefined): string[] =>
    Array.from(new Set(players.map(fn).filter((v): v is string => !!v))).sort();
  return {
    position: uniqSorted((pl) => humanizePositionTemplate(pl.positionTemplate)),
    // League stays a NAME here (ids-2, fork B): the snapshot has no id for most of these
    // leagues, and these widgets don't call the API yet. Ids come with their real data in
    // Track 3 — track2-plan.md §8.6 "Still names".
    league: uniqSorted((pl) => pl.competition),
    club: uniqSorted((pl) => pl.team),
    nationality: uniqSorted((pl) => pl.country),
    foot: uniqSorted(preferredFoot),
  };
}

/** The value a freshly-added filter chip opens on for a reference player. Foot always
 *  defaults to Right-footed regardless of the player (renderers-stats.js:254). */
export function playerFilterDefault(p: KitPlayer, key: string): string | null {
  if (key === "position") return humanizePositionTemplate(p.positionTemplate);
  if (key === "league") return p.competition ?? null;
  if (key === "club") return p.team ?? null;
  if (key === "nationality") return p.country ?? null;
  if (key === "foot") return "Right-footed";
  return null;
}

/**
 * The core predicate: does a player pass every active filter, relative to the reference
 * player (for the player-relative range filters)? Ported verbatim from
 * renderers-stats.js:268 — the categorical gates, then each active range filter (a
 * missing value on either side fails the match; relative filters compare the signed
 * diff against the reference).
 */
export function matchesFilters(
  pl: KitPlayer,
  active: ActiveFilters,
  refPlayer: KitPlayer,
  rangeFilters: RangeFilterConfig = RANGE_FILTERS,
): boolean {
  if (!matchesCategorical(humanizePositionTemplate(pl.positionTemplate), active.position as CategoricalActive | undefined))
    return false;
  if (!matchesCategorical(pl.league ?? pl.competition ?? null, active.league as CategoricalActive | undefined)) return false;
  if (!matchesCategorical(pl.team ?? null, active.club as CategoricalActive | undefined)) return false;
  if (!matchesCategorical(pl.country ?? null, active.nationality as CategoricalActive | undefined)) return false;
  if (!matchesCategorical(preferredFoot(pl), active.foot as CategoricalActive | undefined)) return false;
  for (const key in rangeFilters) {
    if (active[key] === undefined) continue;
    const spec = rangeFilters[key];
    if (!spec) continue;
    const range = active[key] as RangeActive;
    const plValue = spec.getValue(pl);
    if (plValue == null) return false;
    if (spec.relative) {
      const refValue = spec.getValue(refPlayer);
      if (refValue == null) return false;
      const diff = plValue - refValue;
      if (diff < range.min || diff > range.max) return false;
    } else if (plValue < range.min || plValue > range.max) {
      return false;
    }
  }
  return true;
}
